import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import { differenceInCalendarDays, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
const Plan = () => {
	const moduleChartCanvas = useRef(null);
	const ticketTypeChartCanvas = useRef(null);
	const chartInstances = useRef({});

	const timelineData = [
		{
			dates: '15 septembre - 20 septembre ',
			modules: ['société'],
			tasks: ['Gestion simple des sociétés', "Création des serveurs d'emails"],
		},
		{
			dates: '22 septembre - 26 septembre',
			modules: ['utilisateur + Roles', 'modules', 'clientsDM + clientsS2'],
			tasks: [
				'Gestion des accès par module',
				'Gestion utilisateurs et rôles',
				'Gestion simple clients DM et S2',
			],
		},
		{
			dates: '29 septembre - 3 octobre',
			modules: ['sessions', 'stock', 'devis', 'fournisseur + achat', 'comptabilite'],
			tasks: [
				'Sécurisation accessToken',
				'Gestion des sessions',
				'Gestion produit, devis, achat, fournisseur (backend)',
				'Mise en place des relations entre : devis, demande de prix, fournisseur, bon de commande, reception technicien, achat divers, produit (backend)',
				'Sécurisation api comptabilité (token, role, permission)',
			],
		},
		{
			dates: '06 octobre - 10 octobre',
			modules: ['comptabilite', 'stock', 'devis', 'fournisseur'],
			tasks: [
				'Supprimer les tables inutiles et relier les partenaires et sociétés depuis MongoDB',
				'Initialiser les comptes comptables et les taxes pour chaque société',
				'Créer des factures pour la société sélectionnée',
				'Développer la gestion des produits, catégories, entrepôts, devis et fournisseurs (frontend)',
				'Mettre en place les relations entre les modules : devis, stock et fournisseur (frontend)',
			],
		},
		{
			dates: '13 octobre - 17 octobre',
			modules: ['comptabilite', 'stock', 'devis', 'achat'],
			tasks: [
				'Améliorer le processus de création des factures.',
				'Mettre à jour et optimiser la liste des factures par société.',
				'Mettre à jour la gestion des factures d’avoirs pour chaque société.',
				'Mettre à jour la gestion des factures de charges pour chaque société.',
				'Intégrer et améliorer le module de paiement.',
				'Développer la gestion complète des mouvements de produits (frontend).',
				'Mettre en place l’interface du module d’achats et ses principales fonctionnalités (frontend).',
				'Établir les relations entre les modules : devis, demande de prix, fournisseur, bon de commande, réception technicien et achats divers (frontend).',
				'Assurer la cohérence et la synchronisation des données entre les différents modules liés aux achats.',
			],
		},
	];

	const modulePriorityData = [
		{ name: 'Utilisateurs & Roles', percentage: 100, color: 'primary' },
		{ name: 'Sessions & Sécurité', percentage: 100, color: 'primary' },
		{ name: 'Sociétés', percentage: 80, color: 'primary' },
		{ name: 'Clients (DM & S2)', percentage: 100, color: 'primary' },
		{ name: 'Stock', percentage: 100, color: 'info' },
		{ name: 'Devis', percentage: 100, color: 'info' },
		{ name: 'Fournisseur & Achat', percentage: 100, color: 'info' },
		{
			name: 'Comptabilité (Facturation , Comptabilité, Finance)',
			percentage: 90,
			color: 'secondary',
		},
		{ name: 'Planning', percentage: 10, color: 'secondary' },
		{ name: 'Telephony', percentage: 40, color: 'secondary' },
		{ name: 'RH', percentage: 80, color: 'secondary' },
		{ name: 'Gestion IT', percentage: 80, color: 'secondary' },
		{ name: 'Jira', percentage: 80, color: 'secondary' },
	];

	const totalTasks = timelineData.reduce((acc, sprint) => acc + sprint.tasks.length, 0);
	const completedModules = modulePriorityData.filter((m) => m.percentage === 100).length;
	const totalModules = modulePriorityData.length;

	// Calculate overall completion
	const totalPercentage = modulePriorityData.reduce((sum, m) => sum + m.percentage, 0);
	const overallCompletion = (totalPercentage / totalModules).toFixed(1);

	const parseFrenchDate = (str) => {
		return parse(str, 'd MMMM', new Date(), { locale: fr });
	};

	// Calculate total time spent in development (in weeks)
	const timelineWeeks = (() => {
		if (!timelineData.length) return 0;

		// Extract start and end dates
		const startDates = timelineData.map((d) => d.dates.split(' - ')[0]);
		const endDates = timelineData.map((d) => d.dates.split(' - ')[1]);

		const start = parseFrenchDate(startDates[0]);
		const end = parseFrenchDate(endDates[endDates.length - 1]);

		const days = differenceInCalendarDays(end, start) + 1;
		return Math.round(days / 7); // convert to weeks
	})();

	useEffect(() => {
		const styleId = 'dev-timeline-infographic-styles';
		// Prevent duplicate style injection
		if (!document.getElementById(styleId)) {
			const styleElement = document.createElement('style');
			styleElement.id = styleId;
			styleElement.innerHTML = `
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap');
                body {
                    font-family: 'Inter', sans-serif;
                    background-color: #f0f9ff;
                }
                .chart-container {
                    position: relative;
                    width: 100%;
                    max-width: 500px;
                    margin-left: auto;
                    margin-right: auto;
                    height: 320px;
                    max-height: 400px;
                }
                @media (min-width: 768px) {
                    .chart-container {
                        height: 400px;
                    }
                }
                .timeline-item {
                    position: relative;
                }
                .timeline-item::before {
                    content: '';
                    position: absolute;
                    left: -2px;
                    top: 5px;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background-color: #00B4D8;
                    border: 4px solid #CAF0F8;
                    z-index: 1;
                }
                .timeline-line {
                    position: absolute;
                    left: 7px;
                    top: 25px;
                    bottom: 0;
                    width: 2px;
                    background-color: #90E0EF;
                }
                .timeline-item:last-child {
                  padding-bottom: 0 !important;
                }
                .timeline-item:last-child .timeline-line {
                    display: none;
                }
                .text-theme-primary {
                    color: #0077B6;
                }
            `;
			document.head.appendChild(styleElement);
		}

		if (chartInstances.current.moduleChart) {
			chartInstances.current.moduleChart.destroy();
		}
		if (chartInstances.current.ticketTypeChart) {
			chartInstances.current.ticketTypeChart.destroy();
		}

		const brilliantBluesPalette = {
			primary: '#0077B6',
			accent1: '#00B4D8',
			accent2: '#90E0EF',
			accent3: '#CAF0F8',
			text: '#333333',
		};

		const tooltipTitleCallback = (tooltipItems) => {
			const item = tooltipItems[0];
			let label = item.chart.data.labels[item.dataIndex];
			if (Array.isArray(label)) {
				return label.join(' ');
			}
			return label;
		};

		if (moduleChartCanvas.current) {
			const moduleCtx = moduleChartCanvas.current.getContext('2d');
			chartInstances.current.moduleChart = new Chart(moduleCtx, {
				type: 'doughnut',
				data: {
					labels: [
						'Société & Clients',
						'Utilisateurs & Accès',
						'Commercial & Stock',
						'Autres Modules',
					],
					datasets: [
						{
							label: 'Tickets par Module',
							data: [4, 3, 5, 8],
							backgroundColor: [
								brilliantBluesPalette.primary,
								brilliantBluesPalette.accent1,
								brilliantBluesPalette.accent2,
								'#a0dae4',
							],
							borderColor: '#ffffff',
							borderWidth: 4,
							hoverOffset: 8,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							position: 'bottom',
							labels: {
								font: {
									family: "'Inter', sans-serif",
								},
								color: brilliantBluesPalette.text,
							},
						},
						tooltip: {
							callbacks: {
								title: tooltipTitleCallback,
							},
						},
					},
					cutout: '60%',
				},
			});
		}

		if (ticketTypeChartCanvas.current) {
			const ticketTypeCtx = ticketTypeChartCanvas.current.getContext('2d');
			chartInstances.current.ticketTypeChart = new Chart(ticketTypeCtx, {
				type: 'bar',
				data: {
					labels: ['Feature', 'Tech', 'Infra'],
					datasets: [
						{
							label: 'Nombre de Tickets',
							data: [7, 1, 1],
							backgroundColor: brilliantBluesPalette.accent1,
							borderColor: brilliantBluesPalette.primary,
							borderWidth: 2,
							borderRadius: 8,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					indexAxis: 'y',
					plugins: {
						legend: {
							display: false,
						},
						tooltip: {
							callbacks: {
								title: tooltipTitleCallback,
							},
						},
					},
					scales: {
						x: {
							beginAtZero: true,
							grid: {
								display: false,
								drawBorder: false,
							},
							ticks: {
								stepSize: 2,
								color: brilliantBluesPalette.text,
								font: {
									family: "'Inter', sans-serif",
								},
							},
						},
						y: {
							grid: {
								display: false,
								drawBorder: false,
							},
							ticks: {
								color: brilliantBluesPalette.text,
								font: {
									family: "'Inter', sans-serif",
								},
							},
						},
					},
				},
			});
		}

		return () => {
			if (chartInstances.current.moduleChart) {
				chartInstances.current.moduleChart.destroy();
			}
			if (chartInstances.current.ticketTypeChart) {
				chartInstances.current.ticketTypeChart.destroy();
			}
		};
	}, [timelineData]);

	return (
		<PageWrapper isProtected={false} title={'Plan'}>
			<Page container='fluid'>
				<div className='text-dark'>
					<div className=' py-4 py-md-5'>
						<header className='text-center mb-5'>
							<h1 className='display-4 fw-bolder text-theme-primary mb-2'>
								Plan de Développement
							</h1>
							<p className='fs-5 text-muted'>
								Un aperçu visuel des objectifs, des modules et des tâches pour le
								prochain cycle de développement.
							</p>
						</header>

						<section className='row g-4 mb-5 text-center'>
							{/* Completed Modules */}
							<div className='col-sm-2'>
								<div className='bg-white p-4 rounded-4 shadow-sm border h-100 text-center'>
									<p className='display-3 fw-bold text-theme-primary'>
										{completedModules}
									</p>
									<p className='text-muted mt-2'>Modules terminés</p>
								</div>
							</div>
							<div className='col-sm-2'>
								<div className='bg-white p-4 rounded-4 shadow-sm border h-100 text-center'>
									<p className='display-3 fw-bold text-theme-primary'>5</p>
									<p className='text-muted mt-2'>Modules en cours</p>
								</div>
							</div>

							{/* Total Modules / Overall Progress */}
							<div className='col-sm-4'>
								<div className='bg-white p-4 rounded-4 shadow-sm border h-100 text-center'>
									<p className='display-3 fw-bold text-theme-primary'>
										{overallCompletion}%
									</p>
									<p className='text-muted mt-2'>Avancement</p>
								</div>
							</div>

							{/* Total Tasks */}
							<div className='col-sm-4'>
								<div className='bg-white p-4 rounded-4 shadow-sm border h-100 text-center'>
									<p className='display-3 fw-bold text-theme-primary'>
										{timelineWeeks - 1}{' '}
										{timelineWeeks > 1 ? 'semaines' : 'semaine'}
									</p>
									<p className='text-muted mt-2'>Temps passé en développement</p>
								</div>
							</div>
						</section>

						<main className='row g-5'>
							<div className='col-lg-6'>
								<div className='bg-white p-4 p-md-5 rounded-4 shadow-sm border h-100'>
									<h2 className='h2 fw-bold mb-4 text-theme-primary'>
										Chronologie Détaillée
									</h2>
									<div className='position-relative timeline-wrapper'>
										{timelineData.map((item, index) => (
											<div
												key={index}
												className='timeline-item pb-4 ps-4 pt-2'>
												<div className='timeline-line'></div>
												<div className='row w-100'>
													<div className='col-12'>
														<p className='badge text-bg-light fw-semibold fs-6  ms-2'>
															{item.dates}
														</p>
														<ul className='list-unstyled fw-semibold fs-6 mt-1 mb-2'>
															{item.modules.map(
																(module, moduleIndex) => (
																	<li
																		key={moduleIndex}
																		className='text-capitalize mb-2'>
																		• {module}
																	</li>
																),
															)}
														</ul>
													</div>
													<div className='col-12'>
														<ul className='list-unstyled fs-6  mt-2 pt-1'>
															{item.tasks.map((task, taskIndex) => (
																<li key={taskIndex}>- {task}</li>
															))}
														</ul>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
							<div className='col-lg-6'>
								<div className='d-grid gap-5'>
									{/* <div className='bg-white p-4 rounded-4 shadow-sm border'>
										<h2 className='h2 fw-bold mb-4 text-center text-theme-primary'>
											Répartition par Module
										</h2>
										<p className='text-center small text-muted mb-4'>
											Ce graphique montre la distribution de l'effort de
											développement entre les principaux modules priorisés.
										</p>
										<div className='chart-container'>
											<canvas ref={moduleChartCanvas}></canvas>
										</div>
									</div> */}
									<div className='bg-white p-4 rounded-4 shadow-sm border'>
										<h2 className='h2 fw-bold mb-4 text-center text-theme-primary'>
											Priorité des Modules
										</h2>
										<p className='text-center text-muted mb-4'>
											Aperçu de la répartition de l'effort de développement
											prévu pour l'ensemble des modules du projet.
										</p>
										<div>
											{modulePriorityData.map((module, index) => (
												<div key={index} className='mb-3'>
													<div className='d-flex justify-content-between mb-1'>
														<span className=' fw-bold'>
															{module.name}
														</span>
														<span className=''>
															{module.percentage}%
														</span>
													</div>
													<div
														className='progress'
														style={{ height: '12px' }}>
														<div
															className={`progress-bar`}
															role='progressbar'
															style={{
																width: `${module.percentage}%`,
																backgroundColor: ' #0077B6',
															}}
															aria-valuenow={module.percentage}
															aria-valuemin='0'
															aria-valuemax='100'></div>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							</div>
						</main>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Plan;
